import { useEffect, useState } from "react";
import { api, uploadClubDocument } from "@/lib/api";
import { formatDate } from "@/lib/dates";
import { DateField } from "@/components/DateField";
import { toast } from "sonner";
import { CheckCircle, Warning, DownloadSimple, Trash, CircleNotch, Users, CalendarBlank, ClipboardText } from "@phosphor-icons/react";

// Mock GOV = AG's readiness pre-check visit, scheduled before the GOV. Either side proposes, the other confirms.
export function MockGovCard({ clubId, mock, reload, canParticipate = true }) {
  const [date, setDate] = useState(mock?.date || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setDate(mock?.date || ""); }, [mock?.date]);
  const propose = async () => {
    if (!date) { toast.error("Pick a mock-GOV date"); return; }
    setBusy(true);
    try { await api.post(`/clubs/${clubId}/mock-gov`, { date }); toast.success("Mock-GOV date proposed"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const confirm = async () => {
    setBusy(true);
    try { await api.post(`/clubs/${clubId}/mock-gov/confirm`); toast.success("Mock-GOV date confirmed"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const confirmed = mock?.status === "confirmed";
  return (
    <div className="rounded-2xl bg-white p-6" style={{ boxShadow: "0 4px 20px rgba(28,42,58,0.05)" }} data-testid="mock-gov-card">
      <div className="flex items-center gap-2.5">
        <ClipboardText size={20} weight="regular" style={{ color: "#7C4DFF" }} />
        <h2 className="text-[18px] font-bold tracking-tight" style={{ color: "#1B1B2F" }}>Mock GOV (AG readiness pre-check)</h2>
      </div>
      <p className="mt-1 text-[14px] font-medium" style={{ color: "#54565A" }}>
        The AG visits ~5–10 days before the GOV to check the club is ready. One side proposes the date, the other confirms.
      </p>
      {mock?.date ? (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[14px] font-semibold"
            style={{ background: confirmed ? "#E4F7EF" : "#FEF3E2", color: confirmed ? "#0F8A5F" : "#B5730A" }}>
            {formatDate(mock.date)} · {confirmed ? "Confirmed" : "Proposed"}
          </span>
          {!confirmed && mock?.can_confirm && (
            <button data-testid="mock-confirm" disabled={busy} onClick={confirm}
              className="rounded-full px-4 py-2 text-[14px] font-bold text-white" style={{ background: "#7C4DFF" }}>Confirm date</button>
          )}
          {!confirmed && mock?.proposed_by_me && (
            <span data-testid="mock-awaiting" className="text-[13px] font-semibold" style={{ color: "#B5730A" }}>{mock.awaiting_label}</span>
          )}
        </div>
      ) : null}
      {canParticipate ? (
        <div className="mt-4 flex flex-wrap items-end gap-2.5">
          <DateField value={date ? date.slice(0, 10) : ""} onChange={setDate} testid="mock-date" placeholder="Pick a date" />
          <button data-testid="mock-propose" disabled={busy} onClick={propose}
            className="rounded-full px-4 py-2.5 text-[14px] font-bold" style={{ background: "#F1ECFF", color: "#6A3EE8" }}>
            {mock?.date ? "Propose new date" : "Propose date"}
          </button>
        </div>
      ) : (
        <p className="mt-3 text-[13px] font-medium" style={{ color: "#8A8B90" }} data-testid="mock-readonly-note">
          The Mock GOV date is set between the club and the Assistant Governor.
        </p>
      )}
    </div>
  );
}

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline data-entry field for "Fill in the app" / "Both" requirements.
function InfoField({ initial, canEdit, busy, onSave, testid }) {
  const [text, setText] = useState(initial || "");
  useEffect(() => { setText(initial || ""); }, [initial]);
  if (!canEdit) {
    return initial ? (
      <p className="mt-2 whitespace-pre-wrap rounded-lg p-2.5 text-[14px]" style={{ background: "#F5F6FB", color: "#1B1B2F" }} data-testid={testid}>{initial}</p>
    ) : null;
  }
  return (
    <div className="mt-2" data-testid={testid}>
      <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Type the details here…"
        className="w-full rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: "#ECECF3" }} />
      <button data-testid={`${testid}-save`} disabled={busy} onClick={() => onSave(text)}
        className="mt-1 text-[14px] font-semibold text-primary">{busy ? "Saving…" : "Save details"}</button>
    </div>
  );
}

// Shared checklist + files block. canEdit => officer upload/remove; isOfficial => review marks.
export function ChecklistFiles({ clubId, data, reload, itemFilter }) {
  const [busy, setBusy] = useState(false);
  const docsFor = (id) => data.documents.filter((d) => d.item_id === id);
  const reviewsFor = (id) => data.reviews.filter((r) => r.item_id === id);

  const upload = async (itemId, file) => {
    setBusy(true);
    try { await uploadClubDocument(clubId, itemId, file); toast.success("File uploaded"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setBusy(false);
  };
  const remove = async (docId) => {
    setBusy(true);
    try { await api.delete(`/documents/${docId}`); toast.success("File removed"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
    setBusy(false);
  };
  const review = async (itemId, status) => {
    let note = "";
    if (status === "attention") {
      note = window.prompt("Add a correction comment for the club (optional):") || "";
    }
    try { await api.post(`/clubs/${clubId}/checklist/${itemId}/review`, { status, note }); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const infoText = (id) => (data.info_answers?.[id]?.text) || "";
  const saveInfo = async (itemId, text) => {
    setBusy(true);
    try { await api.put(`/clubs/${clubId}/checklist/${itemId}/info`, { text }); toast.success("Saved"); await reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const statusFor = (it) => {
    const rev = reviewsFor(it.item_id);
    const docs = docsFor(it.item_id);
    const provided = docs.length > 0 || infoText(it.item_id).trim().length > 0;
    if (rev.some((r) => r.status === "attention")) return { label: "Needs correction", color: "#C8102E" };
    if (rev.some((r) => r.status === "checked")) return { label: "Accepted", color: "#009739" };
    if (provided) return { label: "Awaiting review", color: "#7A4B00" };
    return { label: "Not done", color: "#8A8A8A" };
  };

  const shown = data.items.filter(itemFilter || (() => true));

  return (
    <div className="space-y-3" data-testid="checklist-files">
      {shown.map((it) => {
        const docs = docsFor(it.item_id);
        const kind = it.kind || "document";
        const showUpload = kind !== "info";
        const showInfo = kind === "info" || kind === "both";
        const done = docs.length > 0 || infoText(it.item_id).trim().length > 0;
        return (
          <div key={it.item_id} className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid={`checklist-item-${it.item_id}`}>
            <div className="flex items-start gap-2">
              {done ? <CheckCircle size={18} weight="fill" style={{ color: "#009739" }} className="mt-0.5 shrink-0" />
                : <Warning size={18} weight="fill" style={{ color: "#B9B9B9" }} className="mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-[16px] flex items-center gap-2 flex-wrap">
                  {it.name}
                  {(() => { const s = statusFor(it); return (
                    <span className="c-chip" data-testid={`status-${it.item_id}`}
                      style={{ background: `${s.color}18`, color: s.color }}>{s.label}</span>
                  ); })()}
                </p>
                {it.instruction && <p className="c-label mt-0.5">{it.instruction}</p>}
                {showInfo && (
                  <InfoField initial={infoText(it.item_id)} canEdit={data.can_edit && !data.completed} busy={busy}
                    onSave={(t) => saveInfo(it.item_id, t)} testid={`info-${it.item_id}`} />
                )}
                {docs.map((d) => (
                  <div key={d.doc_id} className="mt-2 flex items-center gap-2 text-[14px]" data-testid={`doc-${d.doc_id}`}>
                    <a className="text-primary font-semibold inline-flex items-center gap-1" href={`${API_BASE}/documents/${d.doc_id}/download`} target="_blank" rel="noreferrer">
                      <DownloadSimple size={14} weight="bold" /> {d.file_name}
                    </a>
                    {data.can_edit && (
                      <button data-testid={`remove-doc-${d.doc_id}`} disabled={busy} onClick={() => remove(d.doc_id)}
                        className="text-destructive inline-flex items-center gap-0.5"><Trash size={13} weight="bold" /> Remove</button>
                    )}
                  </div>
                ))}
                {reviewsFor(it.item_id).map((r) => (
                  <p key={r.reviewer_id} className="mt-1 text-[13px]" style={{ color: r.status === "checked" ? "#009739" : "#C8102E" }}>
                    {r.status === "checked" ? "Accepted" : "Needs correction"}{r.note ? ` — ${r.note}` : ""}
                  </p>
                ))}
              </div>
              <div className="flex flex-col items-end gap-1.5 shrink-0">
                {showUpload && data.can_edit && !data.completed && (
                  <label className="text-[14px] font-semibold text-primary cursor-pointer" data-testid={`upload-${it.item_id}`}>
                    {busy ? <CircleNotch size={14} className="animate-spin inline" /> : "Add file"}
                    <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.xls,.xlsx,.doc,.docx,.pdf"
                      onChange={(e) => e.target.files[0] && upload(it.item_id, e.target.files[0])} />
                  </label>
                )}
                {data.is_official && !data.completed && (
                  <span className="flex gap-2 text-[13px] font-semibold">
                    <button data-testid={`check-${it.item_id}`} onClick={() => review(it.item_id, "checked")} style={{ color: "#009739" }}>Mark OK</button>
                    <button data-testid={`attn-${it.item_id}`} onClick={() => review(it.item_id, "attention")} style={{ color: "#C8102E" }}>Needs correction</button>
                  </span>
                )}
              </div>
            </div>
          </div>
        );
      })}
      {data.items.length === 0 && <p className="t-support text-muted-foreground">The district hasn't published this year's document list yet.</p>}
    </div>
  );
}

export function GoalsCard({ clubId }) {
  const [g, setG] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const load = () => api.get(`/clubs/${clubId}/goals`).then((r) => { setG(r.data); setForm(r.data.goals || {}); });
  useEffect(() => { load(); }, [clubId]);
  if (!g) return null;
  const save = async () => {
    try { await api.put(`/clubs/${clubId}/goals`, form); toast.success("Goals saved"); setEdit(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const F = [["membership", "Membership"], ["service_projects", "Service projects"], ["foundation", "Foundation giving"], ["other", "Other goals"]];
  return (
    <section className="mt-6 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid="club-goals">
      <div className="flex items-center justify-between">
        <h2 className="c-group-title">Club goals</h2>
        {g.can_edit && !edit && <button data-testid="edit-goals" onClick={() => setEdit(true)} className="text-sm font-semibold text-primary">Edit</button>}
      </div>
      {edit ? (
        <div className="mt-3 space-y-3">
          {F.map(([k, label]) => (
            <div key={k}>
              <p className="text-[14px] font-semibold mb-1">{label}</p>
              <textarea data-testid={`goals-${k}`} rows={2} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="w-full rounded-xl border border-border p-2.5 text-sm focus:outline-none focus:border-primary" />
            </div>
          ))}
          <button data-testid="save-goals" onClick={save} className="px-5 py-2.5 rounded-full text-sm font-bold" style={{ background: "#F7A81B", color: "#3A2A00" }}>Save goals</button>
        </div>
      ) : (
        <div className="mt-2 space-y-1.5">
          {F.map(([k, label]) => g.goals?.[k] && <p key={k} className="t-support"><span className="font-semibold">{label}:</span> {g.goals[k]}</p>)}
          {!F.some(([k]) => g.goals?.[k]) && <p className="t-support text-muted-foreground">No goals entered yet.</p>}
          {g.goals?.updated_at && <p className="c-label" data-testid="goals-updated-by">Last updated by {g.updated_by_name || g.goals.updated_by} on {formatDate(g.goals.updated_at)}</p>}
        </div>
      )}
    </section>
  );
}

export function DiscussionCard({ clubId }) {
  const [d, setD] = useState(null);
  const [cat, setCat] = useState("Membership");
  const [text, setText] = useState("");
  const load = () => api.get(`/clubs/${clubId}/discussion`).then((r) => setD(r.data)).catch(() => setD({ points: [], categories: [] }));
  useEffect(() => { load(); }, [clubId]);
  if (!d) return null;
  const add = async () => {
    try { await api.post(`/clubs/${clubId}/discussion`, { category: cat, text }); setText(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/discussion/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  return (
    <section className="mt-6 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid="discussion-points">
      <h2 className="c-group-title">Discussion points {d.sees_all && <span className="c-label">(all participants)</span>}</h2>
      <div className="mt-2 space-y-2">
        {d.points.map((pt) => (
          <div key={pt.point_id} className="flex items-start gap-2 t-support" data-testid={`point-${pt.point_id}`}>
            <span className="c-chip bg-[#E8F1FA] text-[#0067C8] shrink-0">{pt.category}</span>
            <span className="flex-1">{pt.text}{pt.author_name && <span className="c-label"> — {pt.author_name}</span>}</span>
            {!pt.author_name && <button data-testid={`del-point-${pt.point_id}`} onClick={() => del(pt.point_id)} className="text-destructive text-[13px] font-semibold">Remove</button>}
          </div>
        ))}
        {d.points.length === 0 && <p className="t-support text-muted-foreground">No points yet.</p>}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <select data-testid="point-category" value={cat} onChange={(e) => setCat(e.target.value)}
          className="rounded-full border border-border px-3 py-2 text-sm bg-white">
          {(d.categories || []).map((c) => <option key={c}>{c}</option>)}
        </select>
        <input data-testid="point-text" value={text} onChange={(e) => setText(e.target.value)} placeholder="Add your point"
          className="flex-1 min-w-[160px] rounded-full border border-border px-4 py-2 text-sm focus:outline-none focus:border-primary" />
        <button data-testid="add-point" onClick={add} disabled={!text.trim()}
          className="px-4 py-2 rounded-full bg-primary text-white text-sm font-semibold disabled:opacity-50">Add</button>
      </div>
    </section>
  );
}


// Club & board meetings with attendance. Total is derived automatically:
// club meeting = active members in the repository; board meeting = distinct board members.
export function MeetingsCard({ clubId, kind: fixedKind }) {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ kind: fixedKind || "club", meeting_date: "", members_present: "" });
  const load = () => api.get(`/clubs/${clubId}/meetings`).then((r) => setData(r.data)).catch(() => setData({ meetings: [], summary: {}, can_edit: false }));
  useEffect(() => { load(); }, [clubId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return (
    <section className="rounded-2xl bg-white p-6 shadow-[0_1px_2px_rgba(84,86,90,0.06)]" data-testid="meetings-card">
      <div className="flex items-center justify-center gap-2 py-4 text-[14px] font-medium" style={{ color: "#54565A" }}>
        <CircleNotch size={18} className="animate-spin" /> Loading attendance…
      </div>
    </section>
  );
  const totalFor = (k) => k === "board" ? (data.board_member_total || 0) : (data.club_member_total || 0);
  const shownMeetings = fixedKind ? data.meetings.filter((m) => m.kind === fixedKind) : data.meetings;
  const add = async () => {
    if (!form.meeting_date) { toast.error("Meeting date is required"); return; }
    try { await api.post(`/clubs/${clubId}/meetings`, form); setForm({ kind: fixedKind || "club", meeting_date: "", members_present: "" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const del = async (id) => { try { await api.delete(`/clubs/${clubId}/meetings/${id}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const s = data.summary || {};
  const heading = fixedKind === "board" ? "Board meeting attendance" : fixedKind === "club" ? "Club meeting attendance" : "Club & board meetings";
  return (
    <section className="bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid="meetings-card">
      <h2 className="c-group-title flex items-center gap-2"><CalendarBlank size={16} weight="bold" className="text-primary" /> {heading}</h2>
      {!fixedKind && (
        <div className="mt-2 flex flex-wrap gap-2 text-[13px]">
          <span className="c-chip bg-[#E8F1FA] text-[#0067C8]" data-testid="meetings-club-summary">Club meetings: {s.club_count || 0} · avg {s.club_avg_attendance || 0}%</span>
          <span className="c-chip bg-[#E8F1FA] text-[#0067C8]" data-testid="meetings-board-summary">Board meetings: {s.board_count || 0} · avg {s.board_avg_attendance || 0}%</span>
        </div>
      )}
      <div className="mt-3 space-y-2" data-testid="meetings-list">
        {shownMeetings.map((m) => (
          <div key={m.meeting_id} className="rounded-xl border p-3" style={{ borderColor: "#CBCEDC", background: "#FAFBFD" }} data-testid={`meeting-${m.meeting_id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-[15px] font-semibold">{m.kind === "board" ? "Board meeting" : "Club meeting"}
                <span className="c-label ml-2">{m.meeting_date}</span>
              </p>
              {data.can_edit && <button data-testid={`del-meeting-${m.meeting_id}`} onClick={() => del(m.meeting_id)} className="text-destructive text-[13px] font-semibold inline-flex items-center gap-0.5"><Trash size={12} weight="bold" /> Remove</button>}
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="c-label inline-flex items-center gap-1"><Users size={13} weight="bold" /> {m.members_present}/{m.members_total || "—"}</span>
              <div className="flex-1 h-2.5 rounded-full bg-[#EEF1F5] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${m.attendance_pct}%`, background: "#0067C8" }} data-testid={`meeting-bar-${m.meeting_id}`} />
              </div>
              <span className="text-[13px] font-semibold text-[#0067C8] w-10 text-right">{m.attendance_pct}%</span>
            </div>
          </div>
        ))}
        {shownMeetings.length === 0 && <p className="t-support text-muted-foreground">No meetings recorded yet.</p>}
      </div>
      {data.can_edit && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="meeting-form">
          {!fixedKind && (
            <select data-testid="meeting-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
              className="col-span-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#B7BBCC", color: "#1B1B2F", background: "#fff" }}>
              <option value="club">Club meeting</option>
              <option value="board">Board meeting</option>
            </select>
          )}
          <DateField value={form.meeting_date} onChange={(v) => setForm({ ...form, meeting_date: v })} testid="meeting-date"
            placeholder="Meeting date" className="flex items-center gap-2 rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#B7BBCC", color: "#1B1B2F" }} />
          <input data-testid="meeting-present" type="number" min="0" value={form.members_present} onChange={(e) => setForm({ ...form, members_present: e.target.value })} placeholder="Members present"
            className="rounded-xl border px-3 py-2 text-sm" style={{ borderColor: "#B7BBCC", color: "#1B1B2F" }} />
          <div className="col-span-2 -mt-1 px-1 text-[13px] font-medium" style={{ color: "#54565A" }} data-testid="meeting-total-auto">
            of {totalFor(form.kind)} {form.kind === "board" ? "on the board" : "members in the club"}
          </div>
          <button data-testid="add-meeting" onClick={add} className="col-span-2 px-4 py-2.5 rounded-full bg-primary text-white text-sm font-semibold">Add meeting</button>
        </div>
      )}
    </section>
  );
}

export function MeetingsDialog({ clubId, kind, open, onClose, reload }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open && kind) {
      setLoading(true);
      api.get(`/clubs/${clubId}/meetings`)
        .then((r) => {
          setRows(r.data.meetings.filter((m) => m.kind === kind)
            .map((m) => ({ meeting_id: m.meeting_id, meeting_date: m.meeting_date, members_present: String(m.members_present ?? ""), guests: String(m.guests ?? "") })));
          setTotal(kind === "board" ? r.data.board_member_total : r.data.club_member_total);
          setCanEdit(r.data.can_edit);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, kind, clubId]);
  if (!open) return null;
  const cap = kind === "board" ? 15 : 50;
  const title = kind === "board" ? "Board meeting attendance" : "Club meeting attendance";
  const totalLabel = kind === "board" ? "on the board" : "members in the club";
  const setRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addRow = () => { if (rows.length < cap) setRows([...rows, { meeting_date: "", members_present: "", guests: "" }]); };
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/clubs/${clubId}/meetings/bulk`, { kind, meetings: rows.filter((r) => r.meeting_date) });
      toast.success("Saved"); onClose(); reload && reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  const ROW_BG = ["#F4F1FE", "#EAF8F7"];
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="meetings-dialog" onClick={onClose}>
      <div className="mt-10 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[18px] font-bold" style={{ color: "#1B1B2F" }}>{title}</h2>
        <p className="mt-1 text-[13px] font-medium" style={{ color: "#54565A" }}>Members present is out of {total} {totalLabel} (attendance % uses this only). Guests / special invitees are counted separately.</p>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[14px] font-medium" style={{ color: "#54565A" }}>
            <CircleNotch size={18} className="animate-spin" /> Loading attendance…
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-2" data-testid="meetings-rows">
              {rows.length === 0 && <p className="py-3 text-center text-[14px]" style={{ color: "#54565A" }}>No meetings added yet.</p>}
              {rows.length > 0 && (
                <div className="flex items-center gap-2 px-1 text-[11px] font-semibold uppercase tracking-wide" style={{ color: "#8A8B90" }}>
                  <span className="flex-1">Meeting date</span>
                  <span className="w-[74px] text-center">Present</span>
                  <span className="w-[74px] text-center">Guests</span>
                  {canEdit && <span className="w-5" />}
                </div>
              )}
              {rows.map((r, i) => (
                <div key={i} className="flex items-center gap-2 rounded-xl border p-2.5" style={{ borderColor: "#C7CAD9", background: ROW_BG[i % 2] }} data-testid={`meeting-row-${i}`}>
                  <DateField value={r.meeting_date} onChange={(v) => setRow(i, "meeting_date", v)} testid={`meeting-date-${i}`} placeholder="Meeting date"
                    className="flex flex-1 items-center gap-2 rounded-lg border bg-white px-3 py-2 text-[14px]" style={{ borderColor: "#B7BBCC", color: "#1B1B2F" }} />
                  <input data-testid={`meeting-present-${i}`} type="number" min="0" max={total || undefined} value={r.members_present} disabled={!canEdit}
                    onChange={(e) => setRow(i, "members_present", total ? String(Math.min(Math.max(Number(e.target.value || 0), 0), total)) : e.target.value)} placeholder="0"
                    className="w-[74px] rounded-lg border bg-white px-2.5 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: "#B7BBCC", color: "#1B1B2F" }} />
                  <input data-testid={`meeting-guests-${i}`} type="number" min="0" value={r.guests} disabled={!canEdit}
                    onChange={(e) => setRow(i, "guests", e.target.value)} placeholder="0"
                    className="w-[74px] rounded-lg border bg-white px-2.5 py-2 text-[14px] outline-none focus:border-[#7C4DFF]" style={{ borderColor: "#B7BBCC", color: "#1B1B2F" }} />
                  {canEdit && <button data-testid={`meeting-remove-${i}`} onClick={() => removeRow(i)} className="shrink-0" style={{ color: "#E5484D" }}><Trash size={16} weight="bold" /></button>}
                </div>
              ))}
            </div>
            {canEdit && (
              <button data-testid="meeting-add-row" onClick={addRow} disabled={rows.length >= cap}
                className="mt-3 text-[14px] font-semibold text-primary disabled:opacity-50">+ Add meeting {rows.length >= cap ? `(max ${cap})` : ""}</button>
            )}
          </>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button data-testid="meetings-close" onClick={onClose} className="rounded-full px-5 py-2.5 text-[15px] font-semibold" style={{ color: "#54565A" }}>Close</button>
          {canEdit && !loading && <button data-testid="meetings-save" onClick={save} disabled={busy} className="rounded-full px-5 py-2.5 text-[15px] font-semibold text-white" style={{ background: "#7C4DFF" }}>{busy ? "Saving…" : "Save"}</button>}
        </div>
      </div>
    </div>
  );
}

// Structured editor for sponsored Rotaract / Interact clubs (opened from the document card).
export function SponsoredClubsDialog({ clubId, item, open, onClose, reload }) {
  const [rows, setRows] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (open && item) {
      setLoading(true);
      api.get(`/clubs/${clubId}/sponsored/${item.item_id}`)
        .then((r) => { setRows(r.data.clubs); setCanEdit(r.data.can_edit); })
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open, item, clubId]);
  const set = (i, k, val) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: val } : r));
  const addRow = () => setRows([...rows, { club_id: "", club_name: "", president_name: "", phone: "", email: "" }]);
  const save = async () => {
    setBusy(true);
    try { await api.put(`/clubs/${clubId}/sponsored/${item.item_id}`, { clubs: rows }); toast.success("Saved"); onClose(); reload && reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };
  if (!open || !item) return null;
  const isRotaryClubs = /rotary clubs sponsored/i.test(item.name || "");
  const cols = [["club_name", "Club name"], ["club_id", "Club ID"], ["president_name", "President's name"],
    ["phone", "Phone"], ["email", "Email"], ...(isRotaryClubs ? [["gsr_nca", "GSR / NCA"]] : [])];
  const ROW_BG = ["#F4F1FE", "#EAF8F7"]; // alternating club-row colours
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="sponsored-dialog" onClick={onClose}>
      <div className="mt-10 w-full max-w-2xl rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-[18px] font-bold" style={{ color: "#1B1B2F" }}>{item.name}</h2>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-[14px] font-medium" style={{ color: "#54565A" }}>
            <CircleNotch size={18} className="animate-spin" /> Loading clubs…
          </div>
        ) : (
          <>
            <div className="mt-4 space-y-3">
              {rows.length === 0 && <p className="py-4 text-center text-[14px]" style={{ color: "#54565A" }}>No clubs added yet.</p>}
              {rows.map((r, i) => (
                <div key={i} className="grid grid-cols-1 gap-2 rounded-xl border p-3 sm:grid-cols-2 lg:grid-cols-3"
                  style={{ borderColor: "#C7CAD9", background: ROW_BG[i % 2] }} data-testid={`sponsored-row-${i}`}>
                  {cols.map(([k, ph]) => (
                    <input key={k} data-testid={`sponsored-${i}-${k}`} value={r[k] || ""} disabled={!canEdit} onChange={(e) => set(i, k, e.target.value)}
                      placeholder={ph} className="rounded-lg border px-3 py-2 text-[14px] outline-none focus:border-[#7C4DFF]"
                      style={{ borderColor: "#B7BBCC", color: "#1B1B2F", background: "#fff" }} />
                  ))}
                </div>
              ))}
            </div>
            {canEdit && <button data-testid="sponsored-add-row" onClick={addRow} className="mt-3 text-[14px] font-semibold text-primary">+ Add another club</button>}
          </>
        )}
        <div className="mt-5 flex justify-end gap-3">
          <button data-testid="sponsored-cancel" onClick={onClose} className="rounded-full px-5 py-2.5 text-[15px] font-semibold" style={{ color: "#54565A" }}>Close</button>
          {canEdit && !loading && <button data-testid="sponsored-save" onClick={save} disabled={busy} className="rounded-full px-5 py-2.5 text-[15px] font-semibold text-white" style={{ background: "#7C4DFF" }}>{busy ? "Saving…" : "Save"}</button>}
        </div>
      </div>
    </div>
  );
}

// Structured board & committees read-only view for the Club GOV Report.
export function BoardSummary({ clubId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/clubs/${clubId}/appointments`).then((r) => setData(r.data)).catch(() => setData({ assignments: [], catalogue: [] })); }, [clubId]);
  if (!data) return null;
  const names = Object.fromEntries((data.catalogue || []).map((c) => [c.role_id, c.role_name]));
  const filled = (data.assignments || []).filter((a) => !a.vacant && a.member_name);
  return (
    <section className="mt-6 bg-white rounded-2xl shadow-[0_1px_2px_rgba(84,86,90,0.06)] p-4" data-testid="board-summary">
      <h2 className="c-group-title flex items-center gap-2"><Users size={16} weight="bold" className="text-primary" /> Board &amp; committees</h2>
      <div className="mt-2 space-y-1.5">
        {filled.map((a) => (
          <p key={a.assignment_id} className="t-support" data-testid={`board-role-${a.assignment_id}`}>
            <span className="font-semibold">{a.role_name || names[a.role_id]}:</span> {a.member_name}
            {a.holder_inactive && <span className="c-chip ml-2" style={{ background: "#C8102E18", color: "#C8102E" }}>needs replacement</span>}
          </p>
        ))}
        {filled.length === 0 && <p className="t-support text-muted-foreground">The board has not been built yet.</p>}
      </div>
    </section>
  );
}
